import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import ModalChangeIndicator from 'components/modal-change-indicator';
import ModalSendMessage from 'components/modal-send-message';
import AvisoDeCicloForaDaTela from 'components/ui/aviso-de-ciclo-fora-da-tela';
import Button from 'components/ui/button';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import PageHeader from 'components/ui/page-header';
import SeletorDeCiclo from 'components/ui/seletor-de-ciclo';
import { useAuth } from 'context/AuthContext';
import { useCicloDaTela } from 'hooks/useCicloDaTela';
import { useServerTime } from 'hooks/useServerTime';
import { etapasDoCiclo } from 'pages/MinhaEquipe/calendario-do-ciclo';
import CalendarioDoCiclo from 'pages/MinhaEquipe/components/CalendarioDoCiclo';
import { NotFound } from 'pages/NotFound';
import { FOCO_VISIVEL } from 'utils/foco';

import CartaoDaMinhaRealizacao from './components/CartaoDaMinhaRealizacao';
import CartaoDeParticipacao from './components/CartaoDeParticipacao';
import ChamadaDeCadastro from './components/ChamadaDeCadastro';
import ContadoresDoSemestre from './components/ContadoresDoSemestre';
import SecaoDeExcluidas from './components/SecaoDeExcluidas';
import VazioDoSemestre from './components/VazioDoSemestre';
import {
  CicloDoSemestre,
  contadoresDoSemestre,
  ehGestor,
  Entrega,
  estadoDoCadastro,
  faseDaRealizacao,
  faseMensalDe,
  podeAtualizarIndicador,
  realizacaoEditavel,
  separarRealizacoes,
  Vista,
} from './realizacoes-do-semestre';
import { ROTA_DA_APRECIACAO } from './rotas';
import { rostoDoParticipante } from './identidade-do-participante';
import { usePerfil } from './usePerfil';
import { useRascunho } from './useRascunho';
import { useRealizacoesDoSemestre } from './useRealizacoesDoSemestre';

const RAIZ_DA_PAGINA = /^\/realizacao-destaque\/?$/;

/** Texto do design para o painel de participações, adaptado ao ciclo (premissa da spec, sem fonte no design). */
const INTRODUCAO_DAS_PARTICIPACOES =
  'Realizações Destaque em que você foi indicado como participante neste ciclo. Você pode reduzir o seu percentual de atuação ou sair da realização enquanto a apreciação estiver aberta.';

interface PedidoDeIndicador {
  id: string;
  owner: string;
}

interface PedidoDeAutorizacao {
  managerName: string;
  participantCount: number;
  taskTitle: string;
  managerId: string;
}

const SEGMENTO = `cursor-pointer whitespace-nowrap rounded-jj-md border-0 px-4 py-[9px] font-display text-xs font-bold ${FOCO_VISIVEL}`;

/** Realização Destaque do participante: o ciclo inteiro numa leitura, sem seletor de fase. */
export default function RealizacaoDestaque() {
  const { user, cyclesExtended, cycles } = useAuth();
  const location = useLocation();
  const rede = !!user?.extra?.rede;
  // RDN-21: a Rede não vê a página; a leitura nem sai.
  const chave = rede ? undefined : user?._id;

  const ciclos = useMemo(
    () => ((cyclesExtended ?? []) as unknown as (CicloDoSemestre & { year?: number })[]).filter(ciclo => (ciclo.year ?? 0) > 2022),
    [cyclesExtended],
  );
  // CIC-10 a CIC-12 (no lugar de RDN-03 antigo): o ciclo da página é o ciclo do app; trocar aqui troca o do app.
  const { ciclo, foraDaTela, maisRecente, trocar } = useCicloDaTela(ciclos);
  const cicloId = ciclo?._id;

  const { tarefas, carregando, erro, recarregar, excluidas } = useRealizacoesDoSemestre(chave, ciclo);
  const { perfil, reler: relerPerfil } = usePerfil(chave);
  const { realDatetime: agora, hasTime } = useServerTime();

  const [vista, setVista] = useState<Vista>('minhas');
  const [aviso, setAviso] = useState('');
  const [pedidoDeIndicador, setPedidoDeIndicador] = useState<PedidoDeIndicador>();
  const [pedidoDeAutorizacao, setPedidoDeAutorizacao] = useState<PedidoDeAutorizacao>();

  // RDN-20: ao fechar o cadastro/edição (`:id` no Outlet), a página volta à raiz e relê o ciclo.
  const naRaiz = RAIZ_DA_PAGINA.test(location.pathname);
  const estavaNaRaiz = useRef(naRaiz);
  const paginaRef = useRef<HTMLDivElement>(null);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  // O foco volta ao botão do topo que abre o cadastro; sem ele, ao `h1`. Vale no retorno e de novo quando a
  // releitura termina, porque um cadastro novo troca "Cadastrar" por "Editar" e o botão focado sai da tela.
  const focoAposReleitura = useRef<'inicio' | 'fim' | null>(null);
  const focarTopo = useCallback(() => {
    const botaoDoCadastro = paginaRef.current?.querySelector<HTMLElement>('[data-abre-cadastro]');
    (botaoDoCadastro ?? tituloRef.current)?.focus();
  }, []);
  useEffect(() => {
    if (naRaiz && !estavaNaRaiz.current) {
      recarregar();
      focarTopo();
      focoAposReleitura.current = 'inicio';
    }
    estavaNaRaiz.current = naRaiz;
  }, [naRaiz, recarregar, focarTopo]);
  useEffect(() => {
    if (focoAposReleitura.current === 'inicio' && carregando) focoAposReleitura.current = 'fim';
    else if (focoAposReleitura.current === 'fim' && !carregando) {
      focoAposReleitura.current = null;
      focarTopo();
    }
  });

  const gestor = ehGestor(perfil);
  const vistaAtual: Vista = gestor ? 'participacoes' : vista;
  const { minhas, participacoes } = useMemo(() => separarRealizacoes(tarefas, chave ?? ''), [tarefas, chave]);
  const contadores = useMemo(() => contadoresDoSemestre(tarefas, chave ?? ''), [tarefas, chave]);
  const fases = ciclo?.steps;
  const esperandoLista = carregando && !tarefas.length;
  const podeCadastrar = user?.extra?.task !== false && !perfil?.visualizador;

  const estado =
    hasTime && perfil && !gestor && !esperandoLista && !erro
      ? estadoDoCadastro({
          ciclos,
          cicloSelecionadoId: cicloId,
          minhas,
          agora,
          podeCadastrar,
          editavel: realizacao => realizacaoEditavel(realizacao, faseMensalDe(realizacao.created, fases), chave, agora),
        })
      : null;
  const temRascunho = useRascunho(estado?.tipo === 'aberto-sem-realizacao');

  if (rede) return <NotFound />;
  if (!user) return null;
  // RDN-40/RDN-52: o gestor vê aqui as participações; quem chega da fila (`?vista=participacoes`) volta para ela.
  const veioDaFila = new URLSearchParams(location.search).get('vista') === 'participacoes';

  function trocarDeCiclo(id: string) {
    setAviso('');
    trocar(id);
  }

  function tentarDeNovo() {
    if (perfil === null) relerPerfil();
    recarregar();
  }

  function participacaoConcluida(texto: string) {
    setAviso(texto);
    recarregar();
  }

  const lista = vistaAtual === 'minhas' ? minhas : participacoes;

  function renderizarCartao(realizacao: Entrega) {
    const fase = faseMensalDe(realizacao.created, fases);
    const rotuloDaFase = faseDaRealizacao(realizacao.created, fases);

    if (vistaAtual === 'participacoes') {
      return (
        <CartaoDeParticipacao
          key={realizacao._id}
          data={realizacao}
          fase={fase}
          rotuloDaFase={rotuloDaFase}
          playerInfo={perfil}
          onConcluida={participacaoConcluida}
          onRecarregar={recarregar}
        />
      );
    }

    return (
      <CartaoDaMinhaRealizacao
        key={realizacao._id}
        data={realizacao}
        fase={fase}
        rotuloDaFase={rotuloDaFase}
        playerInfo={perfil}
        editavel={realizacaoEditavel(realizacao, fase, chave, agora)}
        podeAtualizarIndicador={podeAtualizarIndicador(realizacao, fase, chave)}
        onAtualizarIndicador={() => setPedidoDeIndicador({ id: realizacao._id, owner: realizacao.owner })}
        onSolicitarAutorizacao={setPedidoDeAutorizacao}
        onRecarregar={recarregar}
      />
    );
  }

  function renderizarLista() {
    if (foraDaTela) return <AvisoDeCicloForaDaTela ciclo={foraDaTela} disponivel={maisRecente} onVer={trocarDeCiclo} />;
    const falhou = erro || perfil === null;

    if (falhou) {
      return (
        <div className="flex flex-wrap items-center gap-4 rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
          <p className="m-0 flex-1 text-[13px] text-content">Não foi possível carregar as realizações deste ciclo.</p>
          <Button variant="solido" onClick={tentarDeNovo}>
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (perfil === undefined || esperandoLista) return <CartoesCarregando />;
    if (!lista.length) return <VazioDoSemestre vista={vistaAtual} />;
    if (vistaAtual === 'participacoes') {
      return (
        <div className="flex flex-col gap-3 rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
          <p className="m-0 text-xs text-content-soft">{INTRODUCAO_DAS_PARTICIPACOES}</p>
          {lista.map(renderizarCartao)}
        </div>
      );
    }
    return <div className="flex flex-col gap-3">{lista.map(renderizarCartao)}</div>;
  }

  return (
    <>
      <div ref={paginaRef} className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
        <PageHeader ref={tituloRef} titulo="Realização Destaque" voltar={{ to: '/', destino: 'a página inicial' }} />

        {perfil === undefined ? null : gestor ? (
          <div className="flex flex-wrap items-center gap-3.5 rounded-jj-lg bg-brand-green px-5 py-3.5">
            <p className="m-0 flex-[1_1_340px] text-[12.5px] leading-[1.55] text-brand-navy">
              Você está vendo as Realizações Destaque em que foi incluído como <b>participante</b>.
            </p>
            <Link
              to={ROTA_DA_APRECIACAO}
              className={`ml-auto inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-jj-sm border border-brand-blue bg-brand-white px-[18px] py-2.5 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue no-underline hover:text-brand-blue-dark hover:no-underline focus:text-brand-blue ${FOCO_VISIVEL}`}
            >
              {veioDaFila && (
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              )}
              {veioDaFila ? 'Voltar para apreciação' : 'Ir para a apreciação'}
            </Link>
          </div>
        ) : (
          <ChamadaDeCadastro
            estado={estado}
            agora={agora}
            temRascunho={temRascunho}
            onIrParaCicloAtual={trocarDeCiclo}
          />
        )}

        <div className="mt-3.5 flex flex-wrap items-end gap-[18px] rounded-jj-panel bg-brand-white px-[22px] pb-4 pt-[18px] shadow-jj-01">
          <div className="min-w-[180px] flex-[0_1_200px]">
            <label
              htmlFor="rd-ciclo"
              className="mb-[7px] block text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft"
            >
              Ciclo
            </label>
            <SeletorDeCiclo ciclos={ciclos} valor={cicloId} onChange={trocarDeCiclo} rotulo="Ciclo" inputId="rd-ciclo" />
          </div>

          {!gestor && (
            <div className="ml-auto">
              <span id="rd-exibir" className="mb-[7px] block text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft">
                Exibir
              </span>
              <div role="group" aria-labelledby="rd-exibir" className="inline-flex gap-1 rounded-jj-md bg-surface-100 p-1">
                {(['minhas', 'participacoes'] as Vista[]).map(opcao => (
                  <button
                    key={opcao}
                    type="button"
                    aria-pressed={vista === opcao}
                    onClick={() => setVista(opcao)}
                    className={`${SEGMENTO} ${vista === opcao ? 'bg-brand-blue-dark text-brand-yellow' : 'bg-transparent text-content'}`}
                  >
                    {opcao === 'minhas' ? 'Minha realização' : 'Participações'}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <section aria-labelledby="titulo-da-lista">
          <h2 id="titulo-da-lista" className="sr-only">
            {vistaAtual === 'minhas' ? 'Minha realização' : 'Participações'}
          </h2>
          {/* O gestor vê só as participações (RDN-40); os contadores misturariam as realizações que ele cadastrou. */}
          {!gestor && <ContadoresDoSemestre contadores={contadores} />}
          <p role="status" className="m-0 mt-3.5 text-xs font-bold text-brand-blue-dark empty:mt-0">
            {aviso}
          </p>
          <div className="mt-3.5">{renderizarLista()}</div>
        </section>

        {/* Some com a lista principal carregando (inclusive na releitura) ou falha, como o `resolveDeletedSection` do develop (RDD-14). */}
        {vistaAtual === 'minhas' && !carregando && !erro && perfil && chave && (
          <SecaoDeExcluidas
            excluidas={excluidas}
            autor={{ chave, nomeGuerra: perfil?.nome_guerra ?? user?.name, rosto: rostoDoParticipante(user) }}
          />
        )}

        {ciclo?.status === 'open' && hasTime && <CalendarioDoCiclo etapas={etapasDoCiclo(cycles)} agora={agora} />}
      </div>

      {pedidoDeIndicador && (
        <ModalChangeIndicator
          id={pedidoDeIndicador.id}
          owner={pedidoDeIndicador.owner}
          callback={alterou => {
            setPedidoDeIndicador(undefined);
            if (alterou) recarregar();
          }}
        />
      )}

      {pedidoDeAutorizacao && (
        <ModalSendMessage {...pedidoDeAutorizacao} onClose={() => setPedidoDeAutorizacao(undefined)} />
      )}

      <Outlet />
    </>
  );
}
